/**
 * Centralized auth token storage helper.
 *
 * Goals:
 * - Single source of truth for access/refresh tokens and user info.
 * - Backward compatibility with legacy keys.
 * - Minimal surface area for localStorage access in components.
 *
 * Storage strategy:
 * - Prefer ACCESS_TOKEN_KEY as the canonical token key.
 * - Optionally mirror to LEGACY_ACCESS_TOKEN_KEY for backward compatibility.
 * - Support additional legacy aliases used in older modules.
 */

const STORAGE_KEYS = {
  ACCESS_TOKEN_KEY: "accessToken",
  LEGACY_ACCESS_TOKEN_KEY: "token",
  PM_ACCESS_TOKEN_KEY: "pm_access_token",
  REFRESH_TOKEN_KEY: "refreshToken",
  PM_REFRESH_TOKEN_KEY: "pm_refresh_token",
  USER_KEY: "user",
};

const ACCESS_TOKEN_KEYS_PRIORITY = [
  STORAGE_KEYS.ACCESS_TOKEN_KEY,
  STORAGE_KEYS.PM_ACCESS_TOKEN_KEY,
  STORAGE_KEYS.LEGACY_ACCESS_TOKEN_KEY,
];

const REFRESH_TOKEN_KEYS_PRIORITY = [
  STORAGE_KEYS.REFRESH_TOKEN_KEY,
  STORAGE_KEYS.PM_REFRESH_TOKEN_KEY,
];

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures (e.g., private mode or quota)
  }
};

const safeRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures
  }
};

const findFirst = (keys) => {
  for (const key of keys) {
    const value = safeGet(key);
    if (value) return value;
  }
  return null;
};

const setTokenAcrossKeys = (token, keys) => {
  if (!token) return;
  keys.forEach((key) => safeSet(key, token));
};

/**
 * Get the canonical access token.
 * Falls back through legacy keys if needed.
 */
export const getAccessToken = () => findFirst(ACCESS_TOKEN_KEYS_PRIORITY);

/**
 * Set access token and optionally mirror to legacy keys.
 */
export const setAccessToken = (token, { mirrorLegacy = true } = {}) => {
  if (!token) return;
  safeSet(STORAGE_KEYS.ACCESS_TOKEN_KEY, token);
  if (mirrorLegacy) {
    setTokenAcrossKeys(token, [
      STORAGE_KEYS.LEGACY_ACCESS_TOKEN_KEY,
      STORAGE_KEYS.PM_ACCESS_TOKEN_KEY,
    ]);
  }
};

/**
 * Clear access token across all known keys.
 */
export const clearAccessToken = () => {
  ACCESS_TOKEN_KEYS_PRIORITY.forEach(safeRemove);
};

/**
 * Get refresh token (if your backend uses it).
 */
export const getRefreshToken = () => findFirst(REFRESH_TOKEN_KEYS_PRIORITY);

/**
 * Set refresh token and optionally mirror to legacy keys.
 */
export const setRefreshToken = (token, { mirrorLegacy = true } = {}) => {
  if (!token) return;
  safeSet(STORAGE_KEYS.REFRESH_TOKEN_KEY, token);
  if (mirrorLegacy) {
    safeSet(STORAGE_KEYS.PM_REFRESH_TOKEN_KEY, token);
  }
};

/**
 * Clear refresh token across all known keys.
 */
export const clearRefreshToken = () => {
  REFRESH_TOKEN_KEYS_PRIORITY.forEach(safeRemove);
};

/**
 * Get user data (parsed JSON) or null.
 */
export const getUser = () => {
  const raw = safeGet(STORAGE_KEYS.USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Set user data (JSON stringified).
 */
export const setUser = (user) => {
  if (!user) return;
  try {
    safeSet(STORAGE_KEYS.USER_KEY, JSON.stringify(user));
  } catch {
    // Ignore serialization errors
  }
};

/**
 * Clear user data.
 */
export const clearUser = () => {
  safeRemove(STORAGE_KEYS.USER_KEY);
};

/**
 * Clear all auth-related data (access token, refresh token, user).
 */
export const clearAuth = () => {
  clearAccessToken();
  clearRefreshToken();
  clearUser();
};

/**
 * Determine if the user is authenticated based on presence of access token.
 */
export const isAuthenticated = () => Boolean(getAccessToken());

/**
 * Set full auth session in one call.
 */
export const setAuthSession = ({
  accessToken,
  refreshToken,
  user,
  mirrorLegacy = true,
} = {}) => {
  if (accessToken) setAccessToken(accessToken, { mirrorLegacy });
  if (refreshToken) setRefreshToken(refreshToken, { mirrorLegacy });
  if (user) setUser(user);
};

export { STORAGE_KEYS };
